import { FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useErrorBoundary } from '@treams/treams-hooks/src/useErrorBoundary';
import { useFeature } from '@treams/treams-hooks/src/useFeature';
import { useUploadAsset } from '@treams/treams-hooks/src/useUploadAsset';
import { useViewer } from '@treams/treams-hooks/src/useViewer';
import * as graphql from '@treams/treams-network';
import { Button } from '@treams/treams-ui/common/button/Button';
import { Callout } from '@treams/treams-ui/common/Callout';
import { FlexGap } from '@treams/treams-ui/common/FlexGap';
import { GraphQLErrorMessage } from '@treams/treams-ui/common/GraphQLErrorMessage';
import { Modal } from '@treams/treams-ui/common/Modal';
import { Popover } from '@treams/treams-ui/common/Popover';
import { Spinner } from '@treams/treams-ui/common/Spinner';
import { Paragraph, Subheading, Title } from '@treams/treams-ui/common/Text';
import { useToasts } from '@treams/treams-ui/common/Toast';
import { QuillText } from '@treams/treams-ui/form/controls/QuillText';
import { ArrowLeftIcon, MagicWandIcon, MicrophoneIcon } from '@treams/treams-ui/icons';

import FeedbackResponseAnswer from './FeedbackResponseAnswer';
import FeedbackThankYou from './FeedbackThankYou';

const AlreadySent = styled(Box)`
	border-radius: ${(props) => props.theme.tokens.radius.md}px;
	background: ${(props) => props.theme.tokens.warning.background};
	border: 1px solid ${(props) => props.theme.tokens.warning.border};
	color: ${(props) => props.theme.tokens.warning.text};
	font-size: 16px;
`;

export default function FeedbackResponseComposer(props: {
	response: graphql.FeedbackResponse;
	secret?: string;
}) {
	const history = useHistory();
	const { t } = useTranslation();
	const viewer = useViewer();
	const hasVoiceMemos = useFeature(graphql.FeatureName.FeedbackVoiceMemos);
	const hasAI = useFeature(graphql.FeatureName.Ai);
	const [error, attempt] = useErrorBoundary();
	const [lastSaveState, setLastSaveState] = useState<'none' | 'draft' | 'submitted'>('none');
	const [showThankYouModal, setShowThankYouModal] = useState(false);
	const [isEditing, setIsEditing] = useState(false);

	const isFromFeedback = history.location.search.includes('/feedback/given');

	const isShared = props.response.status === graphql.FeedbackResponseStatus.Shared;
	const isInternal = !props.secret;
	const isRecipient =
		!!viewer &&
		props.response.recipient.__typename === 'User' &&
		props.response.recipient.id === viewer.id;
	const beforeReleaseDate = props.response.releaseDate
		? new Date(props.response.releaseDate).getTime() > Date.now()
		: false;
	// Internal respondents can edit an already-shared response until the
	// release date, i.e. while their answers are still hidden from the subject.
	const canEdit = isShared && isInternal && isRecipient && beforeReleaseDate;
	const editable =
		props.response.status === graphql.FeedbackResponseStatus.Draft || (canEdit && isEditing);
	const hasTextQuestions = props.response.questions.some(
		(question) =>
			question.type === graphql.FeedbackQuestionType.Text ||
			question.type === graphql.FeedbackQuestionType.Both,
	);

	const errors = new Set(
		(error?.extensions?.error as any)?.data?.details?.map(
			({ path }: { path: number[] }) => path[1],
		),
	);

	const { addToast } = useToasts();

	const formik = useFormik({
		initialValues: {
			answers: props.response.questions.map(
				({ id }) =>
					props.response.answers?.find(({ questionID }) => questionID === id) ?? {
						questionID: id,
						text: undefined,
						stars: undefined,
						selectedOptionID: undefined,
						competencyLevelIndex: undefined,
						voiceMemo: undefined,
					},
			),
		},
		onSubmit: () => {},
	});

	useEffect(() => {
		if (!formik.dirty) return;

		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			// Required by older browsers to trigger their native confirmation dialog.
			event.returnValue = true;
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [formik.dirty]);

	const [updateInternal, { loading: loadingInternal }] =
		graphql.useUpdateInternalFeedbackMutation();

	const [updateExternal, { loading: loadingExternal }] =
		graphql.useUpdateExternalFeedbackMutation();

	const loading = loadingInternal || loadingExternal;

	const [createAsset] = useUploadAsset();

	const onSave = attempt(async (submit: boolean) => {
		const answers: graphql.FeedbackResponseAnswer[] = [];

		for (const answer of formik.values.answers) {
			const voiceMemo = answer.voiceMemo as
				| graphql.VoiceMemo
				| { blob: Blob; samples: number[]; sampleRate: number };

			if (!voiceMemo || 'asset' in voiceMemo) {
				answers.push(answer);
				continue;
			}

			const asset = await createAsset(voiceMemo.blob);

			answers.push({
				...answer,
				voiceMemo: {
					asset,
					samples: voiceMemo.samples,
					sampleRate: voiceMemo.sampleRate,
				},
			});
		}

		formik.setFieldValue('answers', answers);

		const input: graphql.UpdateInternalFeedbackInput | graphql.UpdateExternalFeedbackInput = {
			responseID: props.response.id,
			answers: answers.map(
				({ questionID, text, stars, selectedOptionID, competencyLevelIndex, voiceMemo }) => ({
					questionID,
					text: text ?? undefined,
					stars: stars ?? undefined,
					selectedOptionID: selectedOptionID ?? undefined,
					competencyLevelIndex,
					voiceMemo: voiceMemo
						? {
								assetID: voiceMemo.asset.id,
								samples: voiceMemo.samples,
								sampleRate: voiceMemo.sampleRate,
							}
						: undefined,
				}),
			),
			status: submit ? graphql.FeedbackResponseStatus.Shared : graphql.FeedbackResponseStatus.Draft,
		};

		if (props.secret) {
			await updateExternal({ variables: { input: { ...input, secret: props.secret } } });
		} else {
			await updateInternal({ variables: { input } });
		}

		formik.resetForm({ values: { answers } });
		setLastSaveState(submit ? 'submitted' : 'draft');
	});

	const onCancelEdit = () => {
		formik.resetForm();
		setIsEditing(false);
	};

	// We're using this effect here to make sure formik has time to update
	// its dirty state.
	useEffect(() => {
		if (lastSaveState === 'draft') {
			addToast(t('feedback.response.draft.success'), {
				appearance: 'success',
				autoDismiss: true,
			});
			setLastSaveState('none');
		}

		if (lastSaveState === 'submitted') {
			setLastSaveState('none');
			// Editing an already-shared response stays on the page instead of redirecting.
			if (isEditing) {
				addToast(t('feedback.response.edit.success'), {
					appearance: 'success',
					autoDismiss: true,
				});
				setIsEditing(false);
				return;
			}

			addToast(t('feedback.response.submit.success'), {
				appearance: 'success',
				autoDismiss: true,
			});
			// For external feedback requests, show ThankYouModal instead of redirecting
			if (props.secret) {
				setShowThankYouModal(true);
			} else {
				const redirect = isFromFeedback ? '/feedback/given' : '/';
				history.push(redirect);
			}
		}
	}, [lastSaveState]);

	if (showThankYouModal) {
		return (
			<Popover visible center>
				<Modal>
					<Modal.Contents>
						<FeedbackThankYou external />
					</Modal.Contents>
				</Modal>
			</Popover>
		);
	}

	return (
		<FormikProvider value={formik}>
			<Prompt message={t('navigation.prompt')} when={formik.dirty} />
			{viewer && isShared && !isEditing && (
				<Box mb={4}>
					<Button
						icon={<ArrowLeftIcon />}
						title={t('feedback.answer.backToOverview')}
						link={`/users/${viewer.id}/feedback/given`}
						type="tertiary"
						ml="-16px"
					/>
				</Box>
			)}

			<Box mb={2}>
				<Box mb={2}>
					<Title>{props.response.title || t('feedback.answer.title')}</Title>
				</Box>

				{props.response.instruction && (
					<>
						<Subheading>{t('feedback.answer.instructionalMessage')}</Subheading>
						<Paragraph>
							<QuillText value={props.response.instruction} />
						</Paragraph>
					</>
				)}
			</Box>

			{props.response.status === graphql.FeedbackResponseStatus.Cancelled && (
				<AlreadySent p={3} mb={4}>
					{t('feedback.answer.cancelled')}
				</AlreadySent>
			)}

			{isShared && !isEditing && (
				<AlreadySent p={3} mb={4}>
					<FlexGap alignItems="center" gap={12}>
						<Box flex="1 1 0" minWidth={0}>
							<Paragraph mb={0}>
								{canEdit
									? t('feedback.answer.alreadySentEditable')
									: t('feedback.answer.alreadySent')}
							</Paragraph>
						</Box>
						{canEdit && (
							<Box flexShrink={0}>
								<Button
									type="secondary"
									title={t('feedback.answer.edit')}
									onClick={() => setIsEditing(true)}
								/>
							</Box>
						)}
					</FlexGap>
				</AlreadySent>
			)}

			{editable && hasTextQuestions && (hasAI || hasVoiceMemos) && (
				<Callout
					mt={3}
					mb={3}
					color="blue"
					icon={hasAI ? <MagicWandIcon /> : <MicrophoneIcon />}
					title={t(hasAI ? 'feedback.hint.ai.title' : 'feedback.hint.voiceMemo.title')}
					description={t(
						hasAI ? 'feedback.hint.ai.description' : 'feedback.hint.voiceMemo.description',
					)}
				/>
			)}

			{error && <GraphQLErrorMessage error={error} />}

			{props.response.questions.map((question, index) => (
				<FeedbackResponseAnswer
					key={question.id}
					error={errors.has(index)}
					index={index}
					question={question}
					answer={!editable && isShared ? formik.values.answers[index] : undefined}
					name={`answers[${index}]`}
					viewerCanUpdate={editable}
					responseID={props.secret ? undefined : props.response.id}
					voiceTranscription={!props.secret}
				/>
			))}

			{editable && (
				<Flex justifyContent="flex-end">
					<Box>
						{loading ? (
							<Box mt="10px">
								<Spinner />
							</Box>
						) : isEditing ? (
							<Flex>
								<Box mr={2}>
									<Button
										type="secondary"
										onClick={onCancelEdit}
										title={t('feedback.answer.cancelEdit')}
									/>
								</Box>
								<Box>
									<Button title={t('feedback.answer.saveChanges')} onClick={() => onSave(true)} />
								</Box>
							</Flex>
						) : (
							<Flex>
								<Box mr={2}>
									<Button
										type="secondary"
										onClick={() => onSave(false)}
										title={t('feedback.answer.saveAsDraft')}
									/>
								</Box>
								<Box>
									<Button title={t('feedback.answer.submit')} onClick={() => onSave(true)} />
								</Box>
							</Flex>
						)}
					</Box>
				</Flex>
			)}
		</FormikProvider>
	);
}
